import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import {
  Caution,
  Dialog,
  ErrorNote,
  Field,
  INPUT,
  PRIMARY_BUTTON,
  WhenAndNote,
  toIsoInstant,
} from '@/features/inventory/registerUi';
import {
  REASON_HIDES_VEHICLE,
  REMOVAL_REASONS,
  useUnassignVehicleDevice,
  type RemovalReason,
} from '@/features/inventory/useInventory';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * TAKE THE TRACKER OUT — SLICE 18g. Mahad commissioned a real tracker, assigned
 * it to a live paying customer's fleet as a test, and found there was no way
 * back. This dialog is the way back.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 🔴 THE REASON IS THE WHOLE POINT OF THE CONTROL, AND IT IS NEVER PRE-SELECTED.
 *
 * It is not a note filed after the fact: it DECIDES WHAT THE CUSTOMER SEES.
 *
 *   removed / moved_to_other_vehicle → the vehicle is WITHDRAWN from their app
 *   maintenance                      → it stays VISIBLE, marked "No tracker"
 *
 * A bare dropdown with a default would make that a guess. So the select starts
 * unchosen, the confirm button is inert until one is picked, and — the part that
 * matters — 🔴 THE CONSEQUENCE IS PRINTED IN PLAIN WORDS BESIDE THE CHOICE,
 * BEFORE the operator confirms, and it changes as they change the reason. An
 * operator who reads "the customer will no longer see this vehicle" and meant
 * "it is away for repair" has been stopped by the screen rather than by a
 * customer's phone call the next morning.
 *
 * 🔴 THE SERVER OWNS THE OUTCOME; THIS ONLY PREDICTS IT. `REASON_HIDES_VEHICLE`
 * here is a mirror of the server's own total `Record`, kept for the sentence
 * above and nothing else. The response carries the server's `hidden`, and this
 * dialog renders THAT afterwards — so if the two copies ever disagree, the
 * operator sees the real outcome instead of the predicted one.
 *
 * 🔴 NO DELETE, ANYWHERE. Mahad, 1 Sep: *"NEVER HARD DELETE. Always retire."*
 * This closes the open assignment; the closed row stays as the record that this
 * tracker was in this vehicle between two instants, the vehicle keeps its trips
 * and its history, and the tracker goes back to stock rather than out of
 * existence.
 *
 * NO ARTBOARD EXISTS FOR THIS DIALOG. None of the 51 canvas screens draws
 * reverse provisioning at all — `1u` mixes devices and SIMs into one table and
 * draws no detail view. The furniture is the SHIPPED admin registers (Tenants,
 * Plans, the device dialogs), said plainly rather than approximated from a pixel
 * source that does not exist.
 */
export function UnassignDeviceDialog({
  vehicleId,
  plate,
  onClose,
  onDone,
}: {
  vehicleId: string;
  plate: string;
  onClose: () => void;
  /** Told the server's own verdict, so the caller can reflect the real outcome. */
  onDone?: (result: { hidden: boolean }) => void;
}) {
  const { t } = useTranslation();
  const unassign = useUnassignVehicleDevice();
  // 🔴 NO DEFAULT. '' is "not chosen yet" and blocks submission — see the header.
  const [reason, setReason] = useState<RemovalReason | ''>('');
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function submit() {
    if (reason === '') return;
    setErrorKey(null);
    const when = toIsoInstant(at);
    try {
      const res = await unassign.mutateAsync({
        vehicleId,
        body: {
          reason,
          ...(when ? { at: when } : {}),
          note: note.trim() || null,
        },
      });
      onDone?.({ hidden: res.hidden });
      onClose();
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  return (
    <Dialog
      title={t('vehicles.unassignTitle', { plate })}
      onClose={onClose}
      footer={
        <button
          type="button"
          className={PRIMARY_BUTTON}
          disabled={reason === '' || unassign.isPending}
          onClick={() => void submit()}
        >
          {unassign.isPending ? t('inventory.saving') : t('vehicles.unassignConfirm')}
        </button>
      }
    >
      <div className="grid gap-4">
        <Field label={t('vehicles.unassignReason')}>
          <select
            className={INPUT}
            value={reason}
            onChange={(e) => setReason(e.target.value as RemovalReason | '')}
            autoFocus
          >
            <option value="">{t('vehicles.unassignReasonPlaceholder')}</option>
            {REMOVAL_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`vehicles.reason.${r}`)}
              </option>
            ))}
          </select>
        </Field>

        {/* 🔴 THE CONSEQUENCE, IN WORDS, BEFORE THE CONFIRM — and it moves with
            the choice. Rendered as a Caution for the two withdrawing reasons
            (something a customer will notice) and as a plain note for
            maintenance (nothing changes for them but a chip). Nothing at all
            until a reason is chosen, because there is no consequence to state. */}
        {reason === '' ? null : REASON_HIDES_VEHICLE[reason] ? (
          <Caution>{t('vehicles.consequence.hidden')}</Caution>
        ) : (
          <p className="rounded-lg border border-raad-border p-3 text-[12.5px] text-raad-muted">
            {t('vehicles.consequence.visible')}
          </p>
        )}

        <WhenAndNote at={at} note={note} onAt={setAt} onNote={setNote} />
      </div>
      {/* No second Cancel: `Dialog` already renders Close in its header, and two
          ways out of one dialog is two things to keep in step. */}
      <ErrorNote errorKey={errorKey} />
    </Dialog>
  );
}
